import { ErreurApi } from '../../api/client.js';
import { accuseArbre, construireArbreProvisoire, peutValiderArbre, validerArbre, type ArbreProvisoire } from './arbre-provisoire.js';
import { genererCompteRendu, type CompteRendu } from './compte-rendu.js';
import type { Client, Extractor, RoleCode, Transcriber } from './contrat.js';
import { SAISIE_VIDE, controlerEntretien, type BilanEntretien, type SaisieEntretien } from './entretien.js';
import { REFERENTIEL_ACCUEIL } from './referentiel.js';

/** `validation` : l'arbre part au dossier ; le bouton est désactivé jusqu'à la fin (aucune double écriture). */
export type PhaseAccueil = 'saisie' | 'transcription' | 'arbre' | 'validation' | 'enregistrement' | 'erreur';

export interface EtatAccueil {
  saisie: SaisieEntretien;
  bilan: BilanEntretien;
  phase: PhaseAccueil;
  arbre: ArbreProvisoire | null;
  accuse: string | null;
  compteRendu: CompteRendu | null;
  retour: string | null;
  /** Entretien enregistré (D16) : dossier où il l'est ; `reference` présente quand le dossier vient d'être créé. */
  enregistrement: { dossierId: string; reference: string | null } | null;
}

export interface DependancesAccueil {
  client: Client;
  /**
   * Client dont la clé d'idempotence est fixée par l'appelant : une même personne (ou un même lien) d'un même
   * arbre repart toujours avec la même clé, et le serveur déduplique une reprise. À défaut, `client`.
   */
  clientAvecCle?: (cle: string) => Client;
  /** Identifiant de la tentative : un par arbre proposé (un vocal déposé). */
  genererTentative?: () => string;
  transcripteur: Transcriber;
  extracteur: Extractor;
  role: RoleCode;
  aujourdhui: () => string;
}

export interface ControleurAccueil {
  getEtat(): EtatAccueil;
  subscribe(ecouteur: () => void): () => void;
  saisir(champ: keyof SaisieEntretien, valeur: string): void;
  deposerVocal(audio: Uint8Array, mime: string): Promise<void>;
  validerArbre(dossierId: string): Promise<void>;
  /** Enregistre l'entretien saisi : sans dossier ouvert, crée le dossier puis son compte rendu ; sinon, le compte rendu seul. */
  enregistrerEntretien(dossierOuvert: string | null): Promise<void>;
}

export interface ControleurAccueilComplet extends ControleurAccueil {
  /** Revient à la saisie après une erreur, sans perdre ce qui a été saisi. */
  reessayer(): void;
}

const MSG_TRANSCRIPTION =
  'Le vocal n’a pas pu être transcrit. Votre saisie est conservée ; déposez de nouveau le vocal ou poursuivez sans lui. Cabinet MAAT';
const MSG_EXTRACTION =
  'Le vocal a été transcrit, mais l’arbre n’a pas pu être proposé. Votre saisie est conservée ; réessayez dans un instant. Cabinet MAAT';
const MSG_ROLE = 'Seule Brigitte, ou la direction du Cabinet MAAT, peut valider cet arbre.';
const MSG_INCONNU = 'Une erreur est survenue. Réessayez dans un instant.';
const MSG_REPONSE = 'Le serveur n’a pas confirmé l’enregistrement. Réessayez dans un instant.';

/** « Marie Louise Dupont » devient prénoms « Marie Louise » et nom « Dupont ». */
function separerNom(nom: string): { firstNames: string; lastName: string } {
  const mots = nom.trim().split(/\s+/);
  const lastName = mots.pop() ?? nom;
  return { firstNames: mots.join(' '), lastName };
}

const nombre = (n: number, mot: string): string => `${n} ${mot}${n > 1 ? 's' : ''}`;

const tentativeParDefaut = (): string =>
  typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;

/**
 * Personne écrite au dossier pendant la tentative en cours. `hypothese` est ce que le SERVEUR a renvoyé :
 * une réponse sans `isHypothesis: false` explicite laisse la personne en hypothèse.
 */
interface PersonneEcrite {
  id: string;
  hypothese: boolean;
}

type Lien = ArbreProvisoire['liens'][number];

/** Contrat `RelationshipCreate` : « parent » est une filiation (from parent de to), « conjoint » une union. */
function corpsRelation(lien: Lien, a: string, b: string) {
  return {
    kind: lien.kind === 'parent' ? ('filiation' as const) : ('union' as const),
    personAId: a,
    personBId: b,
    unionType: null,
    filiationType: null,
    startDate: null,
    endDate: null,
  };
}

/** Lit l'identifiant, la version et l'hypothèse renvoyés ; sans identifiant ni version, rien n'est réputé écrit. */
function lirePersonne(reponse: unknown): PersonneEcrite {
  const r = (reponse ?? {}) as { id?: unknown; rowVersion?: unknown; isHypothesis?: unknown };
  if (typeof r.id !== 'string' || r.id === '' || typeof r.rowVersion !== 'number') throw new ErreurApi(0, 'INCONNUE', MSG_REPONSE);
  return { id: r.id, hypothese: r.isHypothesis !== false };
}

const raisonDe = (e: unknown): string => (e instanceof ErreurApi ? e.messageUtilisateur : MSG_INCONNU);

export function creerControleurAccueil(dep: DependancesAccueil): ControleurAccueilComplet {
  const ecouteurs = new Set<() => void>();
  const touches = new Set<keyof SaisieEntretien>();
  const genererTentative = dep.genererTentative ?? tentativeParDefaut;
  /** Une tentative par arbre proposé (tirée au dépôt du vocal) : ses clés d'idempotence restent les mêmes d'une reprise à l'autre. */
  let tentative = '';
  /** Correspondance ref de l'extraction vers la personne écrite : une reprise ne recrée rien. */
  const ecrites = new Map<string, PersonneEcrite>();
  const liensEcrits = new Set<string>();
  /** Validation en vol : un second clic attend la même écriture au lieu d'en lancer une autre. */
  let enVol: Promise<void> | null = null;
  let etat: EtatAccueil = {
    saisie: SAISIE_VIDE,
    bilan: { valide: false, erreurs: {}, alerteAncien: null },
    phase: 'saisie',
    arbre: null,
    accuse: null,
    compteRendu: null,
    retour: null,
    enregistrement: null,
  };
  /** Une tentative d'enregistrement : la même clé d'idempotence d'un essai à l'autre, jusqu'au succès. */
  let tentativeEnregistrement = '';
  let enregistrementEnVol: Promise<void> | null = null;

  const maj = (partiel: Partial<EtatAccueil>): void => {
    etat = { ...etat, ...partiel };
    ecouteurs.forEach((e) => e());
  };

  const clientPour = (cle: string): Client => (dep.clientAvecCle ? dep.clientAvecCle(cle) : dep.client);
  const cleLien = (l: Lien): string => `${l.kind}:${l.from}:${l.to}`;

  function bilanDe(saisie: SaisieEntretien): BilanEntretien {
    const complet = controlerEntretien(saisie, dep.aujourdhui());
    const erreurs = Object.fromEntries(Object.entries(complet.erreurs).filter(([champ]) => touches.has(champ as keyof SaisieEntretien)));
    return { ...complet, erreurs };
  }

  async function enregistrer(dossierOuvert: string | null): Promise<NonNullable<EtatAccueil['enregistrement']>> {
    const ref = REFERENTIEL_ACCUEIL;
    const s = etat.saisie;
    const type = ref.types.find((t) => t.code === s.typeDemande)!;
    let cible = { dossierId: dossierOuvert ?? '', reference: null as string | null };
    if (!dossierOuvert) {
      const cree = (await clientPour(`entretien:${tentativeEnregistrement}:dossier`).appeler('dossiers.creer', {
        corps: {
          title: type.titreDossier.replace('{proprietaire}', s.proprietaireOrigine.trim()),
          mandantPersonId: null,
          etudeId: null,
          typeCode: type.typeCode,
          ...(s.venuPar ? { venuPar: s.venuPar } : {}),
        },
      })) as { id?: unknown; reference?: unknown };
      if (typeof cree.id !== 'string' || cree.id === '') throw new ErreurApi(0, 'INCONNUE', MSG_REPONSE);
      cible = { dossierId: cree.id, reference: typeof cree.reference === 'string' ? cree.reference : null };
    }
    const lignes = [
      `${ref.questions.type} : ${type.libelle}`,
      `${ref.questions.proprietaire} : ${s.proprietaireOrigine.trim()}`,
      `${ref.questions.naissance} : ${s.dateNaissanceProprietaire.trim()}`,
    ];
    const jour = dep.aujourdhui().slice(0, 10).split('-').reverse().join('/');
    await clientPour(`entretien:${tentativeEnregistrement}:compte-rendu:${cible.dossierId}`).appeler('comptesRendus.creer', {
      parametres: { dossierId: cible.dossierId },
      corps: { kind: 'entretien', title: ref.enregistrement.titreCompteRendu.replace('{date}', jour), sourceText: lignes.join('\n') },
    });
    return cible;
  }

  async function ecrireArbre(arbre: ArbreProvisoire, dossierId: string): Promise<void> {
    const refs = new Set(arbre.personnes.map((p) => p.ref));
    const rattachables = arbre.liens.filter((l) => l.kind !== 'fratrie' && refs.has(l.from) && refs.has(l.to) && l.from !== l.to);
    const fratries = arbre.liens.filter((l) => l.kind === 'fratrie').length;
    const ignores = arbre.liens.length - rattachables.length - fratries;

    // Tout l'arbre est écrit en hypothèse : personnes, puis liens entre leurs identifiants serveur. Aucune levée
    // d'hypothèse : au serveur L32, elle relève de la confirmation d'identité par une pièce (handlers.ts, `modifier`).
    try {
      for (const p of arbre.personnes) {
        if (ecrites.has(p.ref)) continue;
        const reponse = await clientPour(`accueil:${tentative}:personne:${p.ref}`).appeler('genealogie.personnes.creer', {
          parametres: { dossierId },
          corps: { ...separerNom(p.nom), vitalStatus: 'inconnu', isHypothesis: true, role: 'heritier' },
        });
        ecrites.set(p.ref, lirePersonne(reponse));
      }
      for (const l of rattachables) {
        const cle = cleLien(l);
        if (liensEcrits.has(cle)) continue;
        const a = ecrites.get(l.from);
        const b = ecrites.get(l.to);
        if (!a || !b) throw new ErreurApi(0, 'INCONNUE', MSG_REPONSE);
        await clientPour(`accueil:${tentative}:lien:${cle}`).appeler('genealogie.relations.creer', {
          parametres: { dossierId },
          corps: corpsRelation(l, a.id, b.id),
        });
        liensEcrits.add(cle);
      }
    } catch (e) {
      maj({
        phase: 'arbre',
        retour:
          `${nombre(ecrites.size, 'personne')} sur ${arbre.personnes.length} et ${nombre(liensEcrits.size, 'lien')} sur ${rattachables.length} ` +
          `écrits au dossier, tous en hypothèse : aucune personne n’est confirmée. L’arbre reste une hypothèse. ${raisonDe(e)} ` +
          'Relancez la validation : elle reprendra là où elle s’est arrêtée, sans doublon.',
      });
      return;
    }

    const complements = [
      fratries > 0
        ? `${nombre(fratries, 'lien')} de fratrie ${fratries > 1 ? 'ne sont pas enregistrés tels quels' : 'n’est pas enregistré tel quel'} : rattachez ces personnes à leur parent commun dans l’arbre du dossier.`
        : '',
      ignores > 0
        ? `${nombre(ignores, 'lien')} citant une personne absente de l’arbre ${ignores > 1 ? 'n’ont pas été enregistrés' : 'n’a pas été enregistré'}.`
        : '',
    ].filter(Boolean);
    const confirmees = new Set(arbre.personnes.filter((p) => ecrites.get(p.ref)?.hypothese === false).map((p) => p.ref));
    const enHypothese = arbre.personnes.length - confirmees.size;
    maj({
      phase: 'arbre',
      arbre: validerArbre(arbre, dep.role, confirmees),
      accuse: null,
      retour: [
        `Arbre enregistré au dossier : ${nombre(arbre.personnes.length, 'personne')}, ${nombre(liensEcrits.size, 'lien')}.`,
        enHypothese === arbre.personnes.length
          ? 'Toutes les personnes restent en hypothèse jusqu’à confirmation de chaque identité par une pièce.'
          : `${nombre(enHypothese, 'personne')} ${enHypothese > 1 ? 'restent' : 'reste'} en hypothèse jusqu’à confirmation de chaque identité par une pièce.`,
        ...complements,
        'Cabinet MAAT',
      ].join(' '),
    });
  }

  return {
    getEtat: () => etat,
    subscribe(ecouteur) {
      ecouteurs.add(ecouteur);
      return () => void ecouteurs.delete(ecouteur);
    },
    saisir(champ, valeur) {
      touches.add(champ);
      const saisie = { ...etat.saisie, [champ]: valeur } as SaisieEntretien;
      maj({ saisie, bilan: bilanDe(saisie) });
    },
    async deposerVocal(audio, mime) {
      // Un arbre en cours d'écriture n'est pas remplacé sous la validation.
      if (enVol) return;
      maj({ phase: 'transcription', retour: null, arbre: null, accuse: null, compteRendu: null });
      tentative = genererTentative();
      ecrites.clear();
      liensEcrits.clear();
      let transcription;
      try {
        transcription = await dep.transcripteur.transcribe(audio, mime);
      } catch {
        maj({ phase: 'erreur', retour: MSG_TRANSCRIPTION });
        return;
      }
      try {
        const arbre = construireArbreProvisoire(await dep.extracteur.extractFamily(transcription.text));
        maj({
          phase: 'arbre',
          arbre,
          accuse: accuseArbre(arbre),
          compteRendu: genererCompteRendu(transcription, etat.saisie),
        });
      } catch {
        maj({ phase: 'erreur', retour: MSG_EXTRACTION });
      }
    },
    validerArbre(dossierId) {
      if (enVol) return enVol;
      const arbre = etat.arbre;
      if (!arbre || arbre.statut === 'enregistre') return Promise.resolve();
      if (!peutValiderArbre(dep.role)) {
        maj({ retour: MSG_ROLE });
        return Promise.resolve();
      }
      maj({ phase: 'validation', retour: null });
      enVol = ecrireArbre(arbre, dossierId)
        .catch((e: unknown) => maj({ phase: 'arbre', retour: `L’arbre reste une hypothèse. ${raisonDe(e)}` }))
        .finally(() => {
          enVol = null;
        });
      return enVol;
    },
    reessayer() {
      maj({ phase: 'saisie', retour: null });
    },
    enregistrerEntretien(dossierOuvert) {
      if (enregistrementEnVol) return enregistrementEnVol;
      if (etat.enregistrement) return Promise.resolve();
      for (const champ of ['typeDemande', 'proprietaireOrigine', 'dateNaissanceProprietaire'] as const) touches.add(champ);
      const bilan = bilanDe(etat.saisie);
      if (!bilan.valide) {
        maj({ bilan });
        return Promise.resolve();
      }
      tentativeEnregistrement ||= genererTentative();
      maj({ phase: 'enregistrement', retour: null, bilan });
      enregistrementEnVol = enregistrer(dossierOuvert)
        .then((enregistrement) => maj({ phase: 'saisie', enregistrement }))
        .catch((e: unknown) => maj({ phase: 'saisie', retour: `L’entretien n’est pas enregistré. ${raisonDe(e)}` }))
        .finally(() => {
          enregistrementEnVol = null;
        });
      return enregistrementEnVol;
    },
  };
}
